export const TODAY_SIGNAL_MAX_AGE_DAYS = 7

export interface TodaySignal {
  alert_type: string
  old_value: string | null
  new_value: string | null
  created_at: string
  entity_name?: string | null
  entity_category?: string | null
}

const RETAIL_HARDWARE_CATEGORIES = new Set([
  'gear',
  'accessory',
  'accessories',
  'apparel',
  'battery',
  'batteries',
  'merch',
  'merchandise',
])

const RETAIL_HARDWARE_NAME = /\b(?:battery|batteries|charger|charging|hardware|accessory|accessories|apparel|merch|merchandise)\b/i
const PRODUCT_ALERT_TYPES = new Set(['price_drop', 'price_increase', 'new_sku', 'sku_removed'])

export function isRetailHardwareSignal(signal: TodaySignal): boolean {
  if (!PRODUCT_ALERT_TYPES.has(signal.alert_type)) return false
  const category = signal.entity_category?.trim().toLowerCase() ?? ''
  return RETAIL_HARDWARE_CATEGORIES.has(category) || RETAIL_HARDWARE_NAME.test(signal.entity_name ?? '')
}

export function isFreshTodaySignal(signal: TodaySignal, now = Date.now()): boolean {
  const createdAt = new Date(signal.created_at).getTime()
  if (!Number.isFinite(createdAt) || createdAt > now) return false
  return now - createdAt <= TODAY_SIGNAL_MAX_AGE_DAYS * 86_400_000
}

export function isDecisionWorthyTodaySignal(signal: TodaySignal, now = Date.now()): boolean {
  return isFreshTodaySignal(signal, now) && !isRetailHardwareSignal(signal)
}

export function priceMove(signal: TodaySignal): { amount: number; pct: number } | null {
  if (signal.alert_type !== 'price_drop' || !signal.old_value || !signal.new_value) return null
  const oldValue = Number(signal.old_value)
  const newValue = Number(signal.new_value)
  if (!Number.isFinite(oldValue) || !Number.isFinite(newValue) || oldValue <= 0) return null
  return { amount: oldValue - newValue, pct: Math.round(((oldValue - newValue) / oldValue) * 100) }
}

// A transparent triage proxy: meaningful recent price moves first, then promo
// and catalog signals. It never claims revenue, demand, or margin impact.
export function todayImpactScore(signal: TodaySignal): number {
  const move = priceMove(signal)
  if (move) return 1000 + move.pct * 10 + move.amount
  if (signal.alert_type === 'price_increase') return 900
  if (signal.alert_type === 'new_promo') return 800
  if (signal.alert_type === 'new_sku' || signal.alert_type === 'sku_removed') return 500
  return 100
}
